import { useGunsmithStore } from '../store/gunsmithStore';
import { CHASSIS_REGISTRY, COMPONENT_REGISTRY } from '@sstk/weapon-catalogue';
import { listChassisOptions } from '../catalogue/assets';

const CHASSIS_OPTIONS = listChassisOptions();

/** Component/socket assembly: pick the chassis, then mount a catalogued component onto each of its sockets. */
export function AssemblyPanel() {
  const weapons = useGunsmithStore((s) => s.weapons);
  const rosterLoaded = useGunsmithStore((s) => s.rosterLoaded);
  const activeWeaponId = useGunsmithStore((s) => s.activeWeaponId);
  const selectedSocket = useGunsmithStore((s) => s.selectedSocket);
  const setSelectedSocket = useGunsmithStore((s) => s.setSelectedSocket);
  const setActiveChassisId = useGunsmithStore((s) => s.setActiveChassisId);
  const mountComponent = useGunsmithStore((s) => s.mountComponent);
  const unmountComponent = useGunsmithStore((s) => s.unmountComponent);

  const active = weapons.find((w) => w.id === activeWeaponId);

  if (!active) {
    // Same roster fetch RosterPanel reads rosterLoaded for - without this,
    // this message claimed there was nothing to select at the exact moment
    // the toolbar above was still saying "Loading your weapons...".
    return (
      <p style={{ padding: 14, fontSize: 12, color: 'var(--ss-text-3, #6d747e)' }}>
        {rosterLoaded ? 'Select or create a weapon first.' : 'Loading weapons…'}
      </p>
    );
  }

  const chassis = CHASSIS_REGISTRY[active.chassisId];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '12px 14px', overflowY: 'auto', flex: 1 }}>
      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.13em', color: 'var(--ss-text-2, #8b929c)' }}>Chassis</span>
        <select
          className="ss-input"
          value={active.chassisId}
          onChange={(e) => setActiveChassisId(e.target.value)}
          style={{ padding: '7px 10px', fontSize: 12.5, borderRadius: 8 }}
        >
          {CHASSIS_OPTIONS.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.archetype})</option>
          ))}
        </select>
        {!chassis && (
          <p style={{ fontSize: 11.5, color: 'var(--ss-danger, #d0453f)', margin: 0 }}>
            Unknown chassis "{active.chassisId}" - not in the catalogue.
          </p>
        )}
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.13em', color: 'var(--ss-text-2, #8b929c)' }}>
          Sockets{chassis ? ` (${chassis.sockets.length})` : ''}
        </span>
        {chassis && chassis.sockets.length === 0 && (
          <p style={{ fontSize: 11.5, color: 'var(--ss-text-3, #6d747e)', margin: 0 }}>
            This chassis has no catalogued sockets yet - nothing can be mounted on it.
          </p>
        )}
        {chassis?.sockets.map((socket) => {
          const mount = active.components.find((m) => m.socket === socket.id);
          const mountedComponent = mount ? COMPONENT_REGISTRY[mount.componentId] : undefined;
          const compatible = Object.values(COMPONENT_REGISTRY).filter((c) => socket.accepts.includes(c.category));
          const isSelected = selectedSocket === socket.id;
          return (
            <div
              key={socket.id}
              style={{
                border: '1px solid ' + (isSelected ? 'var(--ss-accent-border, #6444d6)' : 'var(--ss-border-soft, rgba(255,255,255,0.08))'),
                borderRadius: 8,
                padding: '8px 10px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                cursor: 'pointer',
              }}
              onClick={() => setSelectedSocket(isSelected ? null : socket.id)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{socket.id}</span>
                <span style={{ fontSize: 10, color: 'var(--ss-text-3, #6d747e)' }}>{socket.accepts.join(', ')}</span>
              </div>
              {mountedComponent ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11.5, color: 'var(--ss-accent, #6444d6)' }}>{mountedComponent.name}</span>
                  <button
                    type="button"
                    className="ss-btn ss-btn--ghost ss-btn--sm"
                    onClick={(e) => { e.stopPropagation(); unmountComponent(socket.id); }}
                  >
                    Unmount
                  </button>
                </div>
              ) : (
                <span style={{ fontSize: 11, color: 'var(--ss-text-3, #6d747e)' }}>Empty</span>
              )}
              {isSelected && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingTop: 4 }}>
                  {compatible.length === 0 && (
                    <span style={{ fontSize: 10.5, color: 'var(--ss-text-3, #6d747e)' }}>No catalogued component fits "{socket.accepts.join('/')}" yet.</span>
                  )}
                  {compatible.map((component) => (
                    <button
                      key={component.id}
                      type="button"
                      className="ss-btn ss-btn--ghost ss-btn--sm"
                      onClick={(e) => { e.stopPropagation(); mountComponent(socket.id, component.id); }}
                      style={mount?.componentId === component.id ? { borderColor: 'var(--ss-accent, #6444d6)', color: 'var(--ss-accent, #6444d6)' } : undefined}
                    >
                      {component.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}
